/**
 * Single-argument value transforms shared across MVVM controls and ad-hoc call sites.
 *
 * The parameter-free transforms take a value and return the transformed value. The parameterized
 * transforms (`regexReplace`, `roundTo`, `clampTo`) are curried: they take their configuration and
 * return a single-argument transform, so every transform ultimately handed to a control has the
 * shape `(value) => value`.
 */

export function trim(value: string): string {
	return value.trim();
}

export function upperCase(value: string): string {
	return value.toUpperCase();
}

export function lowerCase(value: string): string {
	return value.toLowerCase();
}

export function collapseWhitespace(value: string): string {
	return value.replace(/\s+/g, " ");
}

export function removeNonAlphanumeric(value: string): string {
	return value.replace(/[^\p{L}\p{N}]/gu, "");
}

export function removeNonNumeric(value: string): string {
	return value.replace(/[^\p{N}]/gu, "");
}

export function removeWhitespace(value: string): string {
	return value.replace(/\s/g, "");
}

export function removeSpecialChars(value: string): string {
	return value.replace(/[^\p{L}\p{N}\s]/gu, "");
}

export function absolute(value: number): number {
	return Math.abs(value);
}

export function regexReplace(pattern: string, replacement: string, flags?: string): (value: string) => string {
	const regex = new RegExp(pattern, flags || "");
	return function (value: string): string {
		return value.replace(regex, replacement);
	};
}

export function roundTo(decimals: number, mode: "round" | "floor" | "ceil" = "round"): (value: number) => number {
	const factor = Math.pow(10, decimals);
	const fn = mode === "floor" ? Math.floor : mode === "ceil" ? Math.ceil : Math.round;
	return function (value: number): number {
		return fn(value * factor) / factor;
	};
}

/**
 * Clamps a number to an optional lower and/or upper bound. Not delegated to `core`'s `clamp`, which
 * requires both bounds — this must support min-only / max-only / neither.
 */
export function clampTo(min: number | undefined, max: number | undefined): (value: number) => number {
	return function (value: number): number {
		if (max != null && min != null) {
			return Math.max(min, Math.min(max, value));
		}

		if (max != null) {
			return Math.min(max, value);
		}

		if (min != null) {
			return Math.max(min, value);
		}

		return value;
	};
}
